import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import usePermission from "../Util/usePermissions.js";
import useUser from "../Util/useUser.js";
import { getAccessToken } from "../Util/authStorage.js";
import { getWhatsAppUnread, streamWhatsAppEvents } from "../api/whatsappAPI.js";

// One live connection to the WhatsApp Inbox for the whole app (users with
// whatsapp:send): unread counts for the menu badge and the tab title, desktop
// notifications + a sound for new client messages, and an event feed the inbox
// page subscribes to. Falls back to polling /unread while the stream is down.
const FALLBACK_POLL_MS = 30000;
const PREFS_KEY = "wa-inbox-prefs";

const noop = () => () => {};
const LiveContext = createContext({
    connected: false,
    unread: { total: 0, chats: 0 },
    subscribe: noop,
    refreshUnread: () => {},
    setActiveChat: () => {},
    prefs: { notify: false, sound: true },
    setPrefs: () => {},
});

const readPrefs = () => {
    try {
        return { notify: false, sound: true, ...JSON.parse(localStorage.getItem(PREFS_KEY) || "{}") };
    } catch {
        return { notify: false, sound: true };
    }
};

// A short two-note chime made with WebAudio (no audio file to ship).
let audioCtx = null;
export const playChime = () => {
    try {
        audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
        const t = audioCtx.currentTime;
        [[880, 0], [1320, 0.12]].forEach(([freq, delay]) => {
            const osc = audioCtx.createOscillator();
            const gain = audioCtx.createGain();
            osc.type = "sine";
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0.0001, t + delay);
            gain.gain.exponentialRampToValueAtTime(0.18, t + delay + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, t + delay + 0.25);
            osc.connect(gain).connect(audioCtx.destination);
            osc.start(t + delay);
            osc.stop(t + delay + 0.3);
        });
    } catch {
        // audio blocked or unsupported — stay silent
    }
};

const previewOf = (e) => {
    const mime = String(e.media_mime || "");
    if (e.msg_type === "sticker") return "🏷️ Sticker";
    if (mime.startsWith("image/")) return `📷 ${e.preview || "Photo"}`;
    if (mime.startsWith("video/")) return `🎥 ${e.preview || "Video"}`;
    if (mime.startsWith("audio/")) return "🎤 Voice message";
    if (mime) return `📄 ${e.preview || "Document"}`;
    return e.preview || "New message";
};

export function WhatsAppLiveProvider({ children }) {
    const { hasPermission } = usePermission();
    const enabled = hasPermission("whatsapp:send");
    const history = useHistory();
    const { user } = useUser();
    const meRef = useRef(null);
    meRef.current = user?.id || null;
    const [connected, setConnected] = useState(false);
    const [unread, setUnread] = useState({ total: 0, chats: 0 });
    const [prefs, setPrefsState] = useState(readPrefs);
    const listeners = useRef(new Set());
    const activeChat = useRef(null);
    const prefsRef = useRef(prefs);
    prefsRef.current = prefs;

    const setPrefs = useCallback((patch) => {
        setPrefsState((p) => {
            const next = { ...p, ...patch };
            try { localStorage.setItem(PREFS_KEY, JSON.stringify(next)); } catch { /* private mode */ }
            return next;
        });
    }, []);

    const refreshUnread = useCallback(async (signal) => {
        if (!enabled) return;
        try {
            const u = await getWhatsAppUnread({ signal });
            if (u && typeof u.total === "number") setUnread(u);
        } catch {
            // badge only — never toast
        }
    }, [enabled]);

    const subscribe = useCallback((fn) => {
        listeners.current.add(fn);
        return () => listeners.current.delete(fn);
    }, []);

    const setActiveChat = useCallback((number) => { activeChat.current = number || null; }, []);

    // Notify on a client message the user isn't looking at. A chat assigned to a
    // teammate only notifies that teammate; unassigned chats notify everyone.
    const notify = useCallback((e) => {
        if (e.type !== "message" || e.msg_type === "reaction") return;
        if (e.assigned_to && meRef.current && Number(e.assigned_to) !== Number(meRef.current)) return;
        const looking = document.visibilityState === "visible" && activeChat.current === e.number;
        if (looking) return;
        const p = prefsRef.current;
        if (p.sound) playChime();
        if (!p.notify || typeof Notification === "undefined" || Notification.permission !== "granted") return;
        try {
            const n = new Notification(e.name || `+${e.number}`, { body: previewOf(e), tag: `wa-${e.number}`, renotify: true });
            n.onclick = () => {
                window.focus();
                history.push(`/whatsapp/inbox?chat=${encodeURIComponent(e.number)}`);
                n.close();
            };
        } catch {
            // some browsers only allow notifications from a service worker
        }
    }, [history]);

    // The stream, reconnecting with backoff; /unread polling while it's down.
    useEffect(() => {
        if (!enabled) return undefined;
        let stopped = false;
        let ctrl = null;
        let wait = 2000;
        let timer = null;
        let refreshTimer = null;

        const onEvent = (e) => {
            listeners.current.forEach((fn) => { try { fn(e); } catch { /* a page bug must not kill the stream */ } });
            notify(e);
            if (["message", "read"].includes(e.type)) {
                clearTimeout(refreshTimer);
                refreshTimer = setTimeout(() => refreshUnread(), 400);
            }
        };

        const onOpen = () => { setConnected(true); wait = 2000; refreshUnread(); };

        // One connection attempt; when it ends, the next is scheduled with backoff.
        const connect = async () => {
            if (stopped) return;
            const token = getAccessToken();
            ctrl = new AbortController();
            try {
                if (!token) throw Object.assign(new Error("no token"), { status: 401 });
                await streamWhatsAppEvents({ token, signal: ctrl.signal, onEvent, onOpen });
            } catch (err) {
                if (stopped) return;
                // 401: let an axios call refresh the token, then retry.
                if (err?.status === 401) await refreshUnread();
            }
            setConnected(false);
            if (stopped) return;
            timer = setTimeout(connect, wait);
            wait = Math.min(wait * 2, 30000);
        };
        connect();

        const poll = setInterval(() => { if (document.visibilityState === "visible") refreshUnread(); }, FALLBACK_POLL_MS);
        return () => {
            stopped = true;
            ctrl?.abort();
            clearTimeout(timer);
            clearTimeout(refreshTimer);
            clearInterval(poll);
        };
    }, [enabled, notify, refreshUnread]);

    // "(3) Virangi" in the tab while chats are unread.
    useEffect(() => {
        const base = document.title.replace(/^\(\d+\)\s*/, "");
        document.title = enabled && unread.chats ? `(${unread.chats}) ${base}` : base;
    }, [enabled, unread.chats]);

    const value = useMemo(() => ({
        connected, unread, subscribe, refreshUnread, setActiveChat, prefs, setPrefs,
    }), [connected, unread, subscribe, refreshUnread, setActiveChat, prefs, setPrefs]);

    return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

/** Live inbox state; outside the provider it's inert (no badge, no events). */
export default function useWhatsAppLive() {
    return useContext(LiveContext);
}
